import React from "react";
import {
  Route,
  Routes,
  Navigate,
  BrowserRouter,
  useNavigate,
} from "react-router-dom";

import Header from "./components/header";
import loadable from "@loadable/component";

import Login from "./pages/login";
import Sidebar from "./components/sidebar";
import FindMy from "./services/auth";
import CreateUser from "./pages/user/create";
import UpdateUser from "./pages/user/update";

import CircularProgress from "@mui/material/CircularProgress";

import checkRole from "./helpers/check_role";
import StatisticsReports from "./pages/management/statistics_reports";
import ChangePassword from "./pages/updatepass";

const Home = loadable(() => import("./pages/home"));
const User = loadable(() => import("./pages/user"));
const Logs = loadable(() => import("./pages/settings/logs"));
const CreateExportDeclaration = loadable(() => import("./pages/export/create"));
const EditExportDeclaration = loadable(() => import("./pages/export/edit"));
const Kpi = loadable(() => import("./pages/management/kpi"));
const CreateImportDeclaration = loadable(() => import("./pages/import/create"));
const EditImportDeclaration = loadable(() => import("./pages/import/edit"));

const routesList = {
  create_user: {
    title: "Create new user",
    list: [
      {
        Name: "Dashboard",
        Link: "/",
        isActive: false,
      },
      {
        Name: "User",
        Link: "/user",
        isActive: false,
      },
      {
        Name: "New user",
        Link: "/user/create",
        Title: "Create new user",
        isActive: true,
      },
    ],
  },
  user: {
    title: "List of users",
    list: [
      {
        Name: "Dashboard",
        Link: "/",
        isActive: false,
      },
      {
        Name: "User",
        Link: "/user",
        Title: "List of users",
        isActive: true,
      },
    ],
  },
  update_user: {
    title: "Update user",
    list: [
      {
        Name: "Dashboard",
        Link: "/",
        isActive: false,
      },
      {
        Name: "User",
        Link: "/user",
        isActive: false,
      },
      {
        Name: "Update user",
        Link: "/user/:id",
        Title: "Update user",
        isActive: true,
      },
    ],
  },
  dashboard: {
    title: "Dashboard",
    list: [
      {
        Name: "Dashboard",
        Link: "/",
        isActive: true,
      },
    ],
  },
  reporting_plexus: {
    title: "Reporting Plexus",
    list: [
      {
        Name: "Dashboard",
        Link: "/",
        isActive: false,
      },
      {
        Name: "Reporting Plexus",
        Link: "/reporting_plexus",
        Title: "Reporting Plexus",
        isActive: true,
      },
    ],
  },
  create_export: {
    title: "Create Export Declaration",
    list: [
      {
        Name: "Dashboard",
        Link: "/",
        isActive: false,
      },
      {
        Name: "Create Export Declaration",
        Link: "/create_export",
        Title: "Create Export Declaration",
        isActive: true,
      },
    ],
  },
  create_export_edit: {
    title: "View Export Declaration",
    list: [
      {
        Name: "Dashboard",
        Link: "/",
        isActive: false,
      },
      {
        Name: "Create Export Declaration",
        Link: "/create_export",
        Title: "Create Export Declaration",
        isActive: false,
      },
      {
        Name: "View Export Declaration",
        Link: "/create_export/:id",
        Title: "View Export Declaration",
        isActive: true,
      },
    ],
  },
  create_import: {
    title: "Create Import Declaration",
    list: [
      {
        Name: "Dashboard",
        Link: "/",
        isActive: false,
      },
      {
        Name: "Create Import Declaration",
        Link: "/create_import",
        Title: "Create Import Declaration",
        isActive: true,
      },
    ],
  },
  edit_import: {
    title: "Edit Import Declaration",
    list: [
      {
        Name: "Dashboard",
        Link: "/",
        isActive: false,
      },
      {
        Name: "Edit Import Declaration",
        Link: "/edit_import/:id",
        Title: "Edit Import Declaration",
        isActive: true,
      },
    ],
  },
  internal_kpi: {
    title: "Internal KPI",
    list: [
      {
        Name: "Dashboard",
        Link: "/",
        isActive: false,
      },
      {
        Name: "Internal KPI",
        Link: "/internal_kpi",
        Title: "Internal KPI",
        isActive: true,
      },
    ],
  },
  statistics_reports: {
    title: "Statistics Reports",
    list: [
      {
        Name: "Dashboard",
        Link: "/",
        isActive: false,
      },
      {
        Name: "Statistics Reports",
        Link: "/statistics_reports",
        Title: "Statistics Reports",
        isActive: true,
      },
    ],
  },
  profile: {
    title: "Profile",
    list: [
      {
        Name: "Dashboard",
        Link: "/",
        isActive: false,
      },
      {
        Name: "Profile",
        Link: "/profile",
        Title: "Profile",
        isActive: true,
      },
    ],
  },
  logs: {
    title: "Logs",
    list: [
      {
        Name: "Dashboard",
        Link: "/",
        isActive: false,
      },
      {
        Name: "Logs",
        Link: "/logs",
        Title: "Logs",
        isActive: true,
      },
    ],
  },
};

function RequireAuth({ children, isLogged, info, pathName }) {
  const navigator = useNavigate();

  return isLogged ? (
    <>
      <Header info={info} />
      <div>
        <Sidebar info={info} />
        <div className="page-wrapper">
          <div className="route">
            <div className="header-default">{routesList[pathName].title}</div>
            {routesList[pathName].list.map((route, key) => {
              return (
                <span
                  key={key}
                  onClick={() =>
                    route.isActive === false ? navigator(route.Link) : null
                  }
                >
                  <span
                    className={
                      route.isActive ? "header-active" : "header-inactive"
                    }
                  >
                    {route.Name}
                  </span>
                  {key !== routesList[pathName].list.length - 1 && (
                    <span
                      style={{
                        marginInline: 15,
                        color: "rgba(35,43,53, 0.5)",
                      }}
                    >
                      •
                    </span>
                  )}
                </span>
              );
            })}
          </div>
          <div className="content container-fluid">{children}</div>
        </div>
      </div>
    </>
  ) : (
    <Navigate to={"/login"} />
  );
}

const AppRouter = () => {
  const [isLogged, setIsLogged] = React.useState(null);
  const [info, setInfo] = React.useState(null);

  React.useEffect(() => {
    isUserAuthenticated();
  }, []);

  async function isUserAuthenticated() {
    const content = await FindMy();
    if (content.status === "ok") {
      setIsLogged(true);
      setInfo(content.data);
    } else {
      localStorage.removeItem("token");
      setIsLogged(false);
      setInfo(null);
    }
  }

  if (isLogged === null) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="main-wrapper">
      <BrowserRouter>
        <Routes>
          {/* Dashboard start */}
          <Route
            path="/"
            element={
              <RequireAuth
                isLogged={isLogged}
                info={info}
                pathName={"dashboard"}
              >
                <Home info={info} />
              </RequireAuth>
            }
          />
          {/* Dashboard finish */}
          {checkRole(info?.role, "ROLE_SUPER_USER") && (
            <>
              <Route
                path="/user"
                element={
                  <RequireAuth
                    isLogged={isLogged}
                    info={info}
                    pathName={"user"}
                  >
                    <User info={info} />
                  </RequireAuth>
                }
              />

              <Route
                path="/user/create"
                element={
                  <RequireAuth
                    isLogged={isLogged}
                    info={info}
                    pathName={"create_user"}
                  >
                    <CreateUser info={info} />
                  </RequireAuth>
                }
              />

              <Route
                path="/user/:id"
                element={
                  <RequireAuth
                    isLogged={isLogged}
                    info={info}
                    pathName={"update_user"}
                  >
                    <UpdateUser info={info} />
                  </RequireAuth>
                }
              />
            </>
          )}

          {
            <Route
              path="/create_export"
              element={
                <RequireAuth
                  isLogged={isLogged}
                  info={info}
                  pathName={"create_export"}
                >
                  <CreateExportDeclaration />
                </RequireAuth>
              }
            />
          }
          {
            <Route
              path="/create_export/:id"
              element={
                <RequireAuth
                  isLogged={isLogged}
                  info={info}
                  pathName={"create_export_edit"}
                >
                  <EditExportDeclaration
                  // declarationData={declarationData}
                  // data={declarationInfo}
                  />
                </RequireAuth>
              }
            />
          }
          {/* Import start */}
          <Route
            path="/create_import"
            element={
              <RequireAuth
                isLogged={isLogged}
                info={info}
                pathName={"create_import"}
              >
                <CreateImportDeclaration />
              </RequireAuth>
            }
          />
          <Route
            path="/create_import/:id"
            element={
              <RequireAuth
                isLogged={isLogged}
                info={info}
                pathName={"edit_import"}
              >
                <EditImportDeclaration />
              </RequireAuth>
            }
          />
          {/* Management start */}
          {checkRole(info?.role, "ROLE_SUPER_USER") && (
            <>
              <Route
                path="/internal_kpi"
                element={
                  <RequireAuth
                    isLogged={isLogged}
                    info={info}
                    pathName={"internal_kpi"}
                  >
                    <Kpi />
                  </RequireAuth>
                }
              />

              <Route
                path="/statistics_reports"
                element={
                  <RequireAuth
                    isLogged={isLogged}
                    info={info}
                    pathName={"statistics_reports"}
                  >
                    <StatisticsReports />
                  </RequireAuth>
                }
              />
            </>
          )}
          {/* Settings start */}
          {checkRole(info?.role, "ROLE_ADMIN") && (
            <>
              <Route
                path="/logs"
                element={
                  <RequireAuth
                    isLogged={isLogged}
                    info={info}
                    pathName={"logs"}
                  >
                    <Logs />
                  </RequireAuth>
                }
              />
            </>
          )}

          {/* Settings finish */}
          <Route path="/login" element={<Login />} />

          <Route path="/change_password" element={<ChangePassword />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
};

export default AppRouter;
